import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  Me,
  AgentListItem,
  AgentDetail,
  TelemetryHistoryResult,
  DiskHistoryResult,
  UptimeResponse,
  CaseListItem,
  CaseDetail,
  CaseNote,
  PersonListItem,
  PersonDetail,
  UpsertPersonRequest,
  FunctionListItem,
  CloudWorkerListItem,
  CreateCloudWorkerRequest,
  CloudWorkerEnrollmentConfig,
  TagListItem,
  ProviderListItem,
  ProviderDetail,
  CustomerListItem,
  CustomerDetail,
  UserListItem,
  UnknownPing,
  BlockedIp,
  AgentAlertItem,
  UserAssignment,
  CreateAgentRequest,
  UpdateAgentRequest,
  OpenCaseRequest,
  SetCaseStatusRequest,
  CloseCaseRequest,
  BulkCloseCasesRequest,
  PatchUserRequest,
  UpdateAlertItem,
  UpsertProviderRequest,
  UpdateProviderRequest,
  UpsertCustomerRequest,
  UpdateCustomerRequest,
  UpsertFunctionRequest,
  UpdateFunctionRequest,
  UpsertTagRequest,
  UpdateTagRequest,
  AddNoteRequest,
  MaintenanceWindow,
  GlobalMaintenanceWindow,
  CreateMaintenanceRequest,
  UpdateMaintenanceRequest,
  ZammadSettings,
  SaveZammadSettingsRequest,
  ConnectionTestResult,
  CreateZammadTicketResponse,
  MailSettings,
  SaveMailSettings,
  TeamsSettings,
  SaveTeamsSettings,
  GeneralSettings,
  SaveGeneralSettings,
  BrandingSettings,
  SaveBrandingSettings,
  EntraSettings,
  SaveEntraSettings,
  AuthConfig,
  AllowedDomain,
  CreateAllowedDomain,
  UpdateAllowedDomain,
  AgentRequestListItem,
  AgentRequestDetail,
  CreateAgentRequestBody,
  ApproveAgentRequestBody,
  PushSubscribeBody,
  PushSubscriptionItem,
  PushTestResult,
  VmRequestListItem,
  VmRequestDetail,
  CreateVmRequestBody,
  ApproveVmRequestBody,
  TelemetrySample,
  VmListItem,
  VmDetail,
  AuditEntry,
  AuditFilters,
  VmPermissionPerson,
  UserVmPermissionItem,
  MyVmPermissions,
  SelectableUser,
  GrantVmPermissionBody,
  VmCommandBody,
  EnrollmentConfigResponse,
  ResetEnrollmentResponse,
  TimelineItem,
} from './types'

// Befund 35: Mutations, deren Aufrufer Fehler bereits lokal anzeigen (try/catch +
// eigener Toast/Banner bzw. per-Call-onError), unterdrücken damit den globalen
// Fehler-Toast aus dem MutationCache (lib/queryClient.ts) — sonst Doppel-Feedback.
const silenceGlobalError = { silenceGlobalError: true } as const

// ---- Query key registry ----

export const qk = {
  me: ['me'] as const,
  agents: (activeOnly = true) => ['agents', activeOnly] as const,
  agent: (guid: string) => ['agent', guid] as const,
  uptime: (guid: string, range: string) => ['uptime', guid, range] as const,
  agentAlerts: (guid: string) => ['agentAlerts', guid] as const,
  agentUsers: (guid: string) => ['agentUsers', guid] as const,
  cases: (open?: boolean) => ['cases', open] as const,
  case: (id: string) => ['case', id] as const,
  caseNotes: (id: string) => ['caseNotes', id] as const,
  persons: ['persons'] as const,
  person: (id: string) => ['person', id] as const,
  functions: ['functions'] as const,
  cloudWorkers: ['cloud-workers'] as const,
  tags: ['tags'] as const,
  providers: ['providers'] as const,
  provider: (id: string) => ['provider', id] as const,
  customers: ['customers'] as const,
  customer: (id: string) => ['customer', id] as const,
  unknownPings: ['unknownPings'] as const,
  blockedIps: ['blockedIps'] as const,
  maintenance: (guid: string) => ['maintenance', guid] as const,
  allMaintenance: ['maintenance', 'all'] as const,
  zammadSettings: ['zammadSettings'] as const,
  mailSettings: ['mailSettings'] as const,
  teamsSettings: ['teamsSettings'] as const,
  generalSettings: ['generalSettings'] as const,
  brandingSettings: ['brandingSettings'] as const,
  entraSettings: ['entraSettings'] as const,
  authConfig: ['authConfig'] as const,
  allowedDomains: ['allowedDomains'] as const,
  agentRequests: ['agentRequests'] as const,
  agentRequest: (id: string) => ['agentRequests', id] as const,
  vmRequests: ['vmRequests'] as const,
  vmRequest: (id: string) => ['vmRequests', id] as const,
  telemetry: (guid: string) => ['telemetry', guid] as const,
  vms: ['vms'] as const,
  vm: (id: string) => ['vm', id] as const,
  vmPermissions: (id: string) => ['vm-permissions', id] as const,
  myVmPermissions: (id: string) => ['my-vm-permissions', id] as const,
  userVmPermissions: (id: string) => ['user-vm-permissions', id] as const,
  selectableUsers: ['users', 'selectable'] as const,
  hostVms: (guid: string) => ['hostVms', guid] as const,
  audit: (f?: AuditFilters) => ['audit', f ?? {}] as const,
}

// ---- /api/me ----

// Identity is resolved once per app load. retry:false + no auto-refetch keeps the
// query terminal: a 403/401 must not be hammered on every remount (the live bug
// produced ~13 /api/me requests in a couple of seconds during the error redirect).
export const useMe = () =>
  useQuery({
    queryKey: qk.me,
    queryFn: () => api.get<Me>('/api/me'),
    retry: false,
    retryOnMount: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

// ---- /api/agents ----

export const useAgents = (activeOnly = true, refetchInterval: number | false = false) =>
  useQuery({
    queryKey: qk.agents(activeOnly),
    queryFn: () => api.get<AgentListItem[]>(`/api/agents?activeOnly=${activeOnly}`),
    refetchInterval,
    // #103 — keep the live status fresh even when the dashboard tab is not focused;
    // otherwise a down agent lingers as "online" between manual visits.
    refetchIntervalInBackground: true,
  })

export const useAgent = (guid: string) =>
  useQuery({
    queryKey: qk.agent(guid),
    queryFn: () => api.get<AgentDetail>(`/api/agents/${guid}`),
    enabled: !!guid,
  })

export const useCreateAgent = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAgentRequest) => api.post<AgentDetail>('/api/agents', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agents'] }) },
  })
}

export const useUpdateAgent = (guid: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateAgentRequest) => api.put<AgentDetail>(`/api/agents/${guid}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] })
      qc.invalidateQueries({ queryKey: qk.agent(guid) })
    },
  })
}

export const useDeleteAgent = () => {
  const qc = useQueryClient()
  return useMutation({
    // hard=true entfernt den Agenten wirklich (nur ohne verknüpfte Cases), #7.
    mutationFn: ({ guid, hard }: { guid: string; hard?: boolean }) =>
      api.del<void>(`/api/agents/${guid}${hard ? '?hard=true' : ''}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agents'] }) },
  })
}

// Fund 2: Gegenstück zum Deaktivieren — einen deaktivierten Agenten wieder aktivieren.
export const useReactivateAgent = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ guid }: { guid: string }) =>
      api.post<AgentDetail>(`/api/agents/${guid}/reactivate`),
    onSuccess: (_d, { guid }) => {
      qc.invalidateQueries({ queryKey: ['agents'] })
      qc.invalidateQueries({ queryKey: qk.agent(guid) })
    },
  })
}

// #122 — fordert für einen Host einen sofortigen Inventar-Sync an. Der nächste Command-Poll
// des Agenten (~10 s) holt das Signal ab und forciert SyncOnceAsync. Server-Cooldown autoritativ;
// requested=false = bereits angefordert/Cooldown.
export const useRequestInventoryRefresh = (hostGuid: string) =>
  useMutation({
    mutationFn: () => api.post<{ requested: boolean }>(`/api/agents/${hostGuid}/inventory-refresh`),
    meta: silenceGlobalError, // VmDetail toastet selbst (try/catch)
  })

// ---- /api/agents/{guid}/enrollment (V6 Block 7 Phase A, Lead+) ----
// Both calls mint a single-use OTP server-side, so they are mutations (triggered on a
// button click), never a query-on-mount — re-fetching on mount would burn fresh OTPs.
// #148: OTP issuance is an explicit POST (issue-otp); the GET enrollment-config is read-only.

export const useAgentEnrollmentConfig = () =>
  useMutation({
    mutationFn: (guid: string) =>
      api.post<EnrollmentConfigResponse>(`/api/agents/${guid}/issue-otp`),
  })

export const useResetAgentEnrollment = () =>
  useMutation({
    mutationFn: (guid: string) =>
      api.post<ResetEnrollmentResponse>(`/api/agents/${guid}/reset-enrollment`),
  })

// ---- /api/agents/{guid}/uptime ----

export const useUptime = (guid: string, range: string) =>
  useQuery({
    queryKey: qk.uptime(guid, range),
    queryFn: () => api.get<UptimeResponse>(`/api/agents/${guid}/uptime?range=${range}`),
    enabled: !!guid,
  })

// ---- /api/agents/{guid}/timeline (V1.10 §6.1 — Verlauf / Ereignisse) ----

export const useTimeline = (guid: string, type?: string) =>
  useQuery({
    queryKey: ['timeline', guid, type ?? 'all'],
    queryFn: () => api.get<TimelineItem[]>(`/api/agents/${guid}/timeline${type ? `?type=${type}` : ''}`),
    enabled: !!guid,
  })

// ---- /api/agents/{guid}/telemetry (V6 Block 2, read-only) ----
export const useAgentTelemetry = (guid: string, enabled = true) =>
  useQuery({
    queryKey: qk.telemetry(guid),
    queryFn: () => api.get<TelemetrySample[]>(`/api/agents/${guid}/telemetry`),
    enabled: !!guid && enabled,
  })

// ---- /api/agents/{guid}/telemetry/history (V1.13 Parquet-Verlauf) ----
export const useAgentTelemetryHistory = (guid: string, range: string, resolution: string) =>
  useQuery({
    queryKey: ['agent-telemetry-history', guid, range, resolution],
    queryFn: () => api.get<TelemetryHistoryResult>(
      `/api/agents/${guid}/telemetry/history?range=${range}&resolution=${resolution}`),
    enabled: !!guid,
  })

// ---- /api/agents/{guid}/telemetry/disk-history (V1.14 T3 — Disk-Verlauf pro Laufwerk) ----
export const useAgentTelemetryDiskHistory = (guid: string, range: string, resolution: string) =>
  useQuery({
    queryKey: ['agent-telemetry-disk-history', guid, range, resolution],
    queryFn: () => api.get<DiskHistoryResult>(
      `/api/agents/${guid}/telemetry/disk-history?range=${range}&resolution=${resolution}`),
    enabled: !!guid,
  })

// ---- /api/vms (V6 Block 2, read-only) ----
export const useVms = (enabled = true) =>
  useQuery({ queryKey: qk.vms, queryFn: () => api.get<VmListItem[]>('/api/vms'), enabled })

export const useVm = (id: string, refetchInterval: number | false = false) =>
  useQuery({
    queryKey: qk.vm(id),
    queryFn: () => api.get<VmDetail>(`/api/vms/${id}`),
    enabled: !!id,
    refetchInterval,
  })

export const useAgentVms = (guid: string, enabled = true, refetchInterval: number | false = false) =>
  useQuery({
    queryKey: qk.hostVms(guid),
    queryFn: () => api.get<VmListItem[]>(`/api/agents/${guid}/vms`),
    enabled: !!guid && enabled,
    refetchInterval,
  })

// ---- /api/vms permissions (V6 Block 6) ----
export const useVmPermissions = (vmId: string) =>
  useQuery({
    queryKey: qk.vmPermissions(vmId),
    queryFn: () => api.get<VmPermissionPerson[]>(`/api/vms/${vmId}/permissions`),
    enabled: !!vmId,
  })

export const useMyVmPermissions = (vmId: string) =>
  useQuery({
    queryKey: qk.myVmPermissions(vmId),
    queryFn: () => api.get<MyVmPermissions>(`/api/vms/${vmId}/my-permissions`),
    enabled: !!vmId,
  })

export const useUserVmPermissions = (userId: string) =>
  useQuery({
    queryKey: qk.userVmPermissions(userId),
    queryFn: () => api.get<UserVmPermissionItem[]>(`/api/users/${userId}/vm-permissions`),
    enabled: !!userId,
  })

export const useSelectableUsers = (enabled = true) =>
  useQuery({
    queryKey: qk.selectableUsers,
    queryFn: () => api.get<SelectableUser[]>('/api/users/selectable'),
    enabled,
  })

export const useGrantVmPermission = (vmId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: GrantVmPermissionBody) =>
      api.post(`/api/vms/${vmId}/permissions`, body),
    onSuccess: (_d, body) => {
      qc.invalidateQueries({ queryKey: qk.vmPermissions(vmId) })
      qc.invalidateQueries({ queryKey: qk.myVmPermissions(vmId) })
      qc.invalidateQueries({ queryKey: qk.userVmPermissions(body.userId) })
    },
  })
}

export const useRevokeVmPermission = (vmId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: GrantVmPermissionBody) =>
      api.del(`/api/vms/${vmId}/permissions?userId=${encodeURIComponent(body.userId)}&right=${encodeURIComponent(body.right)}`),
    onSuccess: (_d, body) => {
      qc.invalidateQueries({ queryKey: qk.vmPermissions(vmId) })
      qc.invalidateQueries({ queryKey: qk.myVmPermissions(vmId) })
      qc.invalidateQueries({ queryKey: qk.userVmPermissions(body.userId) })
    },
  })
}

// ---- /api/vms/{id}/commands (V6 Block 7 Phase B) ----
// Enqueues a VM action. The server enforces the per-VM right (404 if absent),
// online state (409), and restore name-confirm (422); the FE is UX only.
export const useVmCommand = (vmId: string) =>
  useMutation({
    mutationFn: (body: VmCommandBody) => api.post(`/api/vms/${vmId}/commands`, body),
    meta: silenceGlobalError, // VmList/VmDetail toasten selbst (try/catch)
  })

// ---- /api/audit (V6 Block 3, read-only) ----
export const useAudit = (f: AuditFilters = {}) =>
  useQuery({
    queryKey: qk.audit(f),
    queryFn: () => api.get<AuditEntry[]>(`/api/audit${auditQuery(f)}`),
  })

function auditQuery(f: AuditFilters): string {
  const p = new URLSearchParams()
  if (f.from) p.set('from', f.from)
  if (f.to) p.set('to', f.to)
  if (f.actorUserId) p.set('actorUserId', f.actorUserId)
  if (f.action) p.set('action', f.action)
  if (f.result) p.set('result', f.result)
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ---- /api/agents/{guid}/alerts ----

export const useAgentAlerts = (guid: string) =>
  useQuery({
    queryKey: qk.agentAlerts(guid),
    queryFn: () => api.get<AgentAlertItem[]>(`/api/agents/${guid}/alerts`),
    enabled: !!guid,
  })

export const useUpdateAgentAlerts = (guid: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateAlertItem[]) => api.put<AgentAlertItem[]>(`/api/agents/${guid}/alerts`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.agentAlerts(guid) }) },
  })
}

// ---- /api/agents/{guid}/users ----
// Person-Merge: ersetzt die alte /contacts-Route. Zuordnung von Personen zu Agenten.

export const useAgentUsers = (guid: string) =>
  useQuery({
    queryKey: qk.agentUsers(guid),
    queryFn: () => api.get<UserAssignment[]>(`/api/agents/${guid}/users`),
    enabled: !!guid,
  })

export const useAddAgentUser = (guid: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { userId: string }) => api.post<UserAssignment>(`/api/agents/${guid}/users`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.agentUsers(guid) }) },
  })
}

export const useRemoveAgentUser = (guid: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => api.del<void>(`/api/agents/${guid}/users/${userId}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.agentUsers(guid) }) },
  })
}

// ---- /api/users (Personen = Alarm-Empfänger + ggf. Login-Konto) ----
// Person-Merge: EINE users-Tabelle ersetzt die alte /api/contacts-Liste.

export const usePersons = (activeOnly = true) =>
  useQuery({
    queryKey: [...qk.persons, activeOnly] as const,
    queryFn: () => api.get<PersonListItem[]>(`/api/users?activeOnly=${activeOnly}`),
  })

export const usePerson = (id: string) =>
  useQuery({
    queryKey: qk.person(id),
    queryFn: () => api.get<PersonDetail>(`/api/users/${id}`),
    enabled: !!id,
  })

export const useCreatePerson = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpsertPersonRequest) => api.post<PersonDetail>('/api/users', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.persons }) },
  })
}

export const useUpdatePerson = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpsertPersonRequest) => api.put<PersonDetail>(`/api/users/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.persons })
      qc.invalidateQueries({ queryKey: qk.person(id) })
    },
  })
}

export const useDeletePerson = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.persons }) },
  })
}

// ---- /api/functions ----

export const useFunctions = () =>
  useQuery({ queryKey: qk.functions, queryFn: () => api.get<FunctionListItem[]>('/api/functions') })

export const useCreateFunction = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpsertFunctionRequest) => api.post<FunctionListItem>('/api/functions', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.functions }) },
  })
}

export const useUpdateFunction = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateFunctionRequest) => api.put<FunctionListItem>(`/api/functions/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.functions }) },
  })
}

export const useDeleteFunction = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/functions/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.functions }) },
  })
}

// ---- /api/cloud-workers (V1.9 Cloud-Fleet, Block 1) ----
export const useCloudWorkers = () =>
  useQuery({
    queryKey: qk.cloudWorkers,
    queryFn: () => api.get<CloudWorkerListItem[]>('/api/cloud-workers'),
  })

export const useCreateCloudWorker = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateCloudWorkerRequest) =>
      api.post<CloudWorkerListItem>('/api/cloud-workers', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.cloudWorkers }) },
    meta: silenceGlobalError, // CloudWorkersAdmin zeigt den Fehler im Dialog (try/catch)
  })
}

export const useDeleteCloudWorker = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/cloud-workers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.cloudWorkers }) },
  })
}

// #148: OTP issuance is an explicit POST (issue-otp); the GET enrollment-config is read-only.
export const useCloudWorkerEnrollmentConfig = () =>
  useMutation({
    mutationFn: (id: string) =>
      api.post<CloudWorkerEnrollmentConfig>(`/api/cloud-workers/${id}/issue-otp`),
  })

// ---- /api/tags ----

export const useTags = () =>
  useQuery({ queryKey: qk.tags, queryFn: () => api.get<TagListItem[]>('/api/tags') })

export const useCreateTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpsertTagRequest) => api.post<TagListItem>('/api/tags', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.tags }) },
  })
}

export const useUpdateTag = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateTagRequest) => api.put<TagListItem>(`/api/tags/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.tags }) },
  })
}

export const useDeleteTag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/tags/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.tags }) },
  })
}

// ---- /api/providers ----

export const useProviders = () =>
  useQuery({ queryKey: qk.providers, queryFn: () => api.get<ProviderListItem[]>('/api/providers') })

export const useProvider = (id: string) =>
  useQuery({
    queryKey: qk.provider(id),
    queryFn: () => api.get<ProviderDetail>(`/api/providers/${id}`),
    enabled: !!id,
  })

export const useCreateProvider = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpsertProviderRequest) => api.post<ProviderDetail>('/api/providers', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.providers }) },
  })
}

export const useUpdateProvider = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateProviderRequest) => api.put<ProviderDetail>(`/api/providers/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.providers })
      qc.invalidateQueries({ queryKey: qk.provider(id) })
    },
  })
}

export const useDeleteProvider = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/providers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.providers }) },
  })
}

// ---- /api/customers ----

export const useCustomers = () =>
  useQuery({ queryKey: qk.customers, queryFn: () => api.get<CustomerListItem[]>('/api/customers') })

export const useCustomer = (id: string) =>
  useQuery({
    queryKey: qk.customer(id),
    queryFn: () => api.get<CustomerDetail>(`/api/customers/${id}`),
    enabled: !!id,
  })

export const useCreateCustomer = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpsertCustomerRequest) => api.post<CustomerDetail>('/api/customers', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.customers }) },
  })
}

export const useUpdateCustomer = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateCustomerRequest) => api.put<CustomerDetail>(`/api/customers/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.customers })
      qc.invalidateQueries({ queryKey: qk.customer(id) })
    },
  })
}

export const useDeleteCustomer = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/customers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.customers }) },
  })
}

// ---- /api/users (Login-Verwaltung: role/status) ----

// PATCH role/status (Admin-Login-Verwaltung). Invalidiert die Personenliste, da
// Rolle/Status jetzt Teil der Personen-Darstellung sind (Person-Merge).
export const usePatchUser = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PatchUserRequest) => api.patch<UserListItem>(`/api/users/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.persons }) },
    meta: silenceGlobalError, // PersonsAdmin toastet selbst (per-Call-onError)
  })
}

// Beendet alle aktiven OTP-Login-Sessions dieser Person (Admin). Liefert { revoked: n }.
// Invalidiert die Personenliste (Status-Darstellung). PersonsAdmin toastet selbst.
export const useRevokeUserSessions = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ revoked: number }>(`/api/users/${id}/revoke-sessions`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.persons }) },
    meta: silenceGlobalError,
  })
}

// ---- /api/unknown-pings ----

export const useUnknownPings = () =>
  useQuery({ queryKey: qk.unknownPings, queryFn: () => api.get<UnknownPing[]>('/api/unknown-pings') })

export const useDeleteUnknownPing = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (guid: string) => api.del<void>(`/api/unknown-pings/${guid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.unknownPings }) },
  })
}

export const useDeleteAllUnknownPings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del<void>('/api/unknown-pings'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.unknownPings }) },
  })
}

export const useBlockUnknownPings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { unknownPingIds: string[]; reason?: string }) =>
      api.post('/api/unknown-pings/block', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.unknownPings }) },
  })
}

// ---- /api/blocked-ips (Firewall-Dropliste) ----
export const useBlockedIps = () =>
  useQuery({ queryKey: qk.blockedIps, queryFn: () => api.get<BlockedIp[]>('/api/blocked-ips') })

export const useDeleteBlockedIp = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/blocked-ips/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.blockedIps }) },
  })
}

// ---- /api/cases ----

export const useCases = (open?: boolean) =>
  useQuery({
    queryKey: qk.cases(open),
    queryFn: () => api.get<CaseListItem[]>(`/api/cases${open === undefined ? '' : `?open=${open}`}`),
  })

export const useCase = (id: string) =>
  useQuery({
    queryKey: qk.case(id),
    queryFn: () => api.get<CaseDetail>(`/api/cases/${id}`),
    enabled: !!id,
  })

export const useOpenCase = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OpenCaseRequest) => api.post<CaseDetail>('/api/cases', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cases'] }) },
    meta: silenceGlobalError, // CaseList zeigt den Fehler im Anlegen-Dialog (per-Call-onError)
  })
}

export const useSetCaseStatus = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SetCaseStatusRequest) => api.put<CaseDetail>(`/api/cases/${id}/status`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: qk.case(id) })
    },
  })
}

export const useCloseCase = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CloseCaseRequest) => api.post<CaseDetail>(`/api/cases/${id}/close`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: qk.case(id) })
    },
  })
}

export const useBulkCloseCases = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BulkCloseCasesRequest) => api.post<void>('/api/cases/close-bulk', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cases'] }) },
  })
}

// ---- /api/cases/{caseId}/notes ----

export const useAddNote = (caseId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AddNoteRequest) => api.post<CaseNote>(`/api/cases/${caseId}/notes`, body),
    // Invalidate BOTH the standalone notes list AND the case detail: CaseDetail renders
    // notes from useCase().data.notes (which carries authorName), so the detail query must
    // refresh after a note is posted.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.caseNotes(caseId) })
      qc.invalidateQueries({ queryKey: qk.case(caseId) })
    },
  })
}

// ---- /api/agents/{guid}/maintenance ----

export const useAgentMaintenance = (guid: string) =>
  useQuery({
    queryKey: qk.maintenance(guid),
    queryFn: () => api.get<MaintenanceWindow[]>(`/api/agents/${guid}/maintenance`),
    enabled: !!guid,
  })

// Global overview across all agents (#19). Visibility-scoped server-side.
export const useAllMaintenance = () =>
  useQuery({
    queryKey: qk.allMaintenance,
    queryFn: () => api.get<GlobalMaintenanceWindow[]>('/api/maintenance'),
  })

// The mutation hooks take the agent guid per-call so the global view can act on
// windows belonging to different agents. Both the per-agent and the global query
// keys are invalidated on success.
const invalidateMaintenance = (qc: ReturnType<typeof useQueryClient>, guid: string) => {
  qc.invalidateQueries({ queryKey: qk.maintenance(guid) })
  qc.invalidateQueries({ queryKey: qk.allMaintenance })
}

export const useCreateMaintenance = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ guid, body }: { guid: string; body: CreateMaintenanceRequest }) =>
      api.post<MaintenanceWindow>(`/api/agents/${guid}/maintenance`, body),
    onSuccess: (_d, { guid }) => invalidateMaintenance(qc, guid),
  })
}

export const useUpdateMaintenance = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ guid, id, body }: { guid: string; id: string; body: UpdateMaintenanceRequest }) =>
      api.put<MaintenanceWindow>(`/api/agents/${guid}/maintenance/${id}`, body),
    onSuccess: (_d, { guid }) => invalidateMaintenance(qc, guid),
  })
}

export const useDeleteMaintenance = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ guid, id }: { guid: string; id: string }) =>
      api.del<void>(`/api/agents/${guid}/maintenance/${id}`),
    onSuccess: (_d, { guid }) => invalidateMaintenance(qc, guid),
  })
}

export const useRegeneratePublicToken = (agentGuid: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<{ token: string }>(`/api/agents/${agentGuid}/regenerate-public-token`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.agent(agentGuid) })
    },
  })
}

// ---- /api/agent-requests ----

export const useCreateAgentRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAgentRequestBody) => api.post<{ id: string }>('/api/agent-requests', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.agentRequests }) },
  })
}

export const useMyAgentRequests = () =>
  useQuery({
    queryKey: [...qk.agentRequests, 'mine'],
    queryFn: () => api.get<AgentRequestListItem[]>('/api/agent-requests/mine'),
  })

export const useAgentRequests = (status?: string, enabled = true) =>
  useQuery({
    queryKey: [...qk.agentRequests, 'list', status ?? 'all'],
    queryFn: () => api.get<AgentRequestListItem[]>(`/api/agent-requests${status ? `?status=${status}` : ''}`),
    enabled,
  })

export const useAgentRequest = (id: string) =>
  useQuery({
    queryKey: qk.agentRequest(id),
    queryFn: () => api.get<AgentRequestDetail>(`/api/agent-requests/${id}`),
    enabled: !!id,
  })

export const useApproveAgentRequest = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ApproveAgentRequestBody) =>
      api.post<{ agentGuid: string }>(`/api/agent-requests/${id}/approve`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.agentRequests }) },
  })
}

export const useRejectAgentRequest = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) =>
      api.post(`/api/agent-requests/${id}/reject`, { reason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.agentRequests }) },
  })
}

// ---- /api/vm-requests (V6 Block 5) ----

export const useCreateVmRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateVmRequestBody) => api.post<{ id: string }>('/api/vm-requests', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.vmRequests }) },
  })
}

export const useMyVmRequests = () =>
  useQuery({
    queryKey: [...qk.vmRequests, 'mine'],
    queryFn: () => api.get<VmRequestListItem[]>('/api/vm-requests/mine'),
  })

export const useVmRequests = (status?: string, enabled = true) =>
  useQuery({
    queryKey: [...qk.vmRequests, 'list', status ?? 'all'],
    queryFn: () => api.get<VmRequestListItem[]>(`/api/vm-requests${status ? `?status=${status}` : ''}`),
    enabled,
  })

export const useVmRequest = (id: string) =>
  useQuery({
    queryKey: qk.vmRequest(id),
    queryFn: () => api.get<VmRequestDetail>(`/api/vm-requests/${id}`),
    enabled: !!id,
  })

export const useApproveVmRequest = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ApproveVmRequestBody) =>
      api.post<{ vmId: string }>(`/api/vm-requests/${id}/approve`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.vmRequests })
      qc.invalidateQueries({ queryKey: qk.vms })
    },
  })
}

export const useRejectVmRequest = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (reason: string) => api.post(`/api/vm-requests/${id}/reject`, { reason }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.vmRequests }) },
  })
}

// ---- /api/public/status/{token} (AllowAnonymous, no Bearer) ----
// Uses publicGet (unauthenticated fetch), NOT the Bearer-authed `api` client.
// ESM import declarations are hoisted, so these bottom-of-file imports are valid.
import { publicGet } from './publicClient'
import type { PublicStatus } from './types'

export const usePublicStatus = (token: string) =>
  useQuery({
    queryKey: ['publicStatus', token] as const,
    queryFn: () => publicGet<PublicStatus>(`/api/public/status/${token}`),
    enabled: !!token,
    retry: false,
  })

// ---- /api/settings/zammad ----

export const useZammadSettings = () =>
  useQuery({
    queryKey: qk.zammadSettings,
    queryFn: () => api.get<ZammadSettings>('/api/settings/zammad'),
  })

export const useSaveZammadSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveZammadSettingsRequest) =>
      api.put<void>('/api/settings/zammad', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.zammadSettings }) },
  })
}

export const useTestZammadConnection = () =>
  useMutation({
    mutationFn: () =>
      api.post<ConnectionTestResult>('/api/settings/zammad/test', {}),
    meta: silenceGlobalError, // Testergebnis wird inline angezeigt (per-Call-onError)
  })

// ---- /api/settings/mail ----

export const useMailSettings = () =>
  useQuery({
    queryKey: qk.mailSettings,
    queryFn: () => api.get<MailSettings>('/api/settings/mail'),
  })

export const useSaveMailSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveMailSettings) => api.put<void>('/api/settings/mail', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.mailSettings }) },
  })
}

export const useTestMail = () =>
  useMutation({
    mutationFn: (recipient: string) =>
      api.post<ConnectionTestResult>('/api/settings/mail/test', { recipient }),
    meta: silenceGlobalError, // Testergebnis wird inline angezeigt (per-Call-onError)
  })

// ---- /api/settings/teams ----

export const useTeamsSettings = () =>
  useQuery({
    queryKey: qk.teamsSettings,
    queryFn: () => api.get<TeamsSettings>('/api/settings/teams'),
  })

export const useSaveTeamsSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveTeamsSettings) => api.put<void>('/api/settings/teams', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.teamsSettings }) },
  })
}

export const useTestTeams = () =>
  useMutation({
    mutationFn: (recipient: string) =>
      api.post<ConnectionTestResult>('/api/settings/teams/test', { recipient }),
    meta: silenceGlobalError, // Testergebnis wird inline angezeigt (per-Call-onError)
  })

// ---- /api/settings/general ----

export const useGeneralSettings = () =>
  useQuery({
    queryKey: qk.generalSettings,
    queryFn: () => api.get<GeneralSettings>('/api/settings/general'),
  })

export const useSaveGeneralSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveGeneralSettings) => api.put<void>('/api/settings/general', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.generalSettings }) },
  })
}

// ---- /api/settings/branding (V1.14 White-Label, Admin, gated custom_branding) ----

export const useBrandingSettings = () =>
  useQuery({
    queryKey: qk.brandingSettings,
    queryFn: () => api.get<BrandingSettings>('/api/settings/branding'),
    // BrandingPane remountet das Formular bei jeder Datenänderung (key=JSON(data)).
    // Ohne langes staleTime würde ein Fokus-Refetch (Default: refetchOnWindowFocus)
    // nach 15 s Staleness die ungespeicherte Eingabe verwerfen. Branding wird
    // ohnehin nach dem Speichern explizit invalidiert ⇒ kein Hintergrund-Refetch nötig.
    staleTime: Infinity,
  })

export const useSaveBrandingSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveBrandingSettings) => api.put<void>('/api/settings/branding', body),
    onSuccess: () => {
      // Admin-Settings-Query auffrischen UND die anonyme ['branding']-Query, die der
      // BrandingProvider live in CSS/App-Name injiziert ⇒ Vorschau sofort aktuell.
      qc.invalidateQueries({ queryKey: qk.brandingSettings })
      qc.invalidateQueries({ queryKey: ['branding'] })
    },
  })
}

// ---- /api/settings/entra + /api/auth/config + /api/admin/restart (V1.6 — Microsoft Entra) ----

export const useEntraSettings = () =>
  useQuery({
    queryKey: qk.entraSettings,
    queryFn: () => api.get<EntraSettings>('/api/settings/entra'),
  })

export const useSaveEntraSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: SaveEntraSettings) => api.put<void>('/api/settings/entra', body),
    onSuccess: () => {
      // The active (startup-resolved) login config may now differ from the DB —
      // refresh both so the "Neustart erforderlich"-Banner can compare them.
      qc.invalidateQueries({ queryKey: qk.entraSettings })
      qc.invalidateQueries({ queryKey: qk.authConfig })
    },
  })
}

export const useTestEntra = () =>
  useMutation({
    mutationFn: () => api.post<ConnectionTestResult>('/api/settings/entra/test', {}),
    meta: silenceGlobalError, // Testergebnis wird inline angezeigt (per-Call-onError)
  })

// Anonymous endpoint — the ACTIVE login config resolved at startup.
export const useAuthConfig = () =>
  useQuery({
    queryKey: qk.authConfig,
    queryFn: () => api.get<AuthConfig>('/api/auth/config'),
  })

export const useAdminRestart = () =>
  useMutation({
    mutationFn: () => api.post<void>('/api/admin/restart'),
  })

// ---- /api/push (Phase 15 — Mobile Push) ----

export const useVapidKey = () =>
  useQuery({
    queryKey: ['vapidKey'] as const,
    queryFn: () => api.get<{ publicKey: string }>('/api/push/vapid-key'),
    staleTime: Infinity, // VAPID public key is static per server lifetime — don't refetch per navigation
  })

export const usePushSubscribe = () =>
  useMutation({
    mutationFn: (b: PushSubscribeBody) => api.post('/api/push/subscribe', b),
    meta: silenceGlobalError, // PushSettings zeigt Fehler im Banner (try/catch)
  })

export const usePushUnsubscribe = () =>
  useMutation({
    // The backend endpoint binds SubscribeBody; only endpoint is read for unsubscribe.
    mutationFn: (endpoint: string) => api.post('/api/push/unsubscribe', { endpoint, p256dh: '', auth: '' }),
    meta: silenceGlobalError, // PushSettings zeigt Fehler im Banner (try/catch)
  })

export const usePushSubscriptions = () =>
  useQuery({
    queryKey: ['pushSubscriptions'] as const,
    queryFn: () => api.get<PushSubscriptionItem[]>('/api/push/subscriptions'),
  })

export const usePushTest = () =>
  useMutation({
    mutationFn: () => api.post<PushTestResult>('/api/push/test'),
    meta: silenceGlobalError, // PushSettings zeigt Fehler im Banner (try/catch)
  })

export const usePushRemoveSubscription = () => {
  const qc = useQueryClient()
  return useMutation({
    // Server-Unsubscribe per Endpoint (gleicher Endpunkt wie usePushUnsubscribe).
    mutationFn: (endpoint: string) => api.post('/api/push/unsubscribe', { endpoint, p256dh: '', auth: '' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pushSubscriptions'] }) },
    meta: silenceGlobalError, // PushSettings zeigt Fehler im Banner (try/catch)
  })
}

// ---- /api/cases/{id}/zammad ----

export const useCreateZammadTicket = (caseId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<CreateZammadTicketResponse>(`/api/cases/${caseId}/zammad`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.case(caseId) }) },
  })
}

// ---- /api/settings/allowed-domains (V1.6 — Anmeldedomänen) ----

export const useAllowedDomains = () =>
  useQuery({
    queryKey: qk.allowedDomains,
    queryFn: () => api.get<AllowedDomain[]>('/api/settings/allowed-domains'),
  })

export const useCreateDomain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAllowedDomain) =>
      api.post<AllowedDomain>('/api/settings/allowed-domains', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.allowedDomains }) },
    meta: silenceGlobalError, // AllowedDomainsPane zeigt Fehler im Banner (per-Call-onError)
  })
}

export const useUpdateDomain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateAllowedDomain }) =>
      api.put<void>('/api/settings/allowed-domains/' + id, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.allowedDomains }) },
    meta: silenceGlobalError, // AllowedDomainsPane zeigt Fehler im Banner (per-Call-onError)
  })
}

export const useDeleteDomain = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>('/api/settings/allowed-domains/' + id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.allowedDomains }) },
    meta: silenceGlobalError, // AllowedDomainsPane zeigt Fehler im Banner (per-Call-onError)
  })
}
